/**
 * The comment composer grows from 44px to 178px when it is focused, and the newest
 * comment ends up hidden underneath it. The cause is different either side of the
 * 768px breakpoint, which is why this file handles two things rather than one.
 *
 * Desktop: the composer is a flex sibling, so the conversation pane genuinely gives
 * up the 134px and the two stay flush - measured overlap 0px. What goes wrong is that
 * the pane keeps its scroll position while it shrinks, so a reader sitting at the
 * bottom loses the last 134px of the conversation off the bottom edge.
 *
 * Mobile: the composer is `position: fixed`, so it floats over the pane instead of
 * taking space from it. Measured at 500px, a focused composer covered 252px of the pane
 * and hid ~150px of the last comment. The pane is given a bottom margin the height of
 * the composer, so it ends where the composer starts - its content and its scrollbar
 * alike. Padding was used first, and cleared the content but left the scrollbar running
 * on underneath the composer to the bottom of the screen. With the margin in place the
 * pane does shrink, so the re-pinning above applies here too.
 *
 * ResizeObserver fires after layout, by which point clientHeight has already shrunk,
 * so "was the reader at the bottom" cannot be measured - it has to be reconstructed
 * from how much height the pane just lost.
 *
 * Growth is deliberately left alone. When the composer collapses the pane gains space
 * and simply reveals more of the conversation, which needs no correction, and
 * re-pinning there would yank a reader out of the history they had scrolled back to.
 */

/** Matches the threshold `renderActivityData` already uses to decide the same question. */
export const NEAR_BOTTOM_PX = 100;

export type ScrollMetrics = {
  scrollHeight: number;
  scrollTop: number;
  clientHeight: number;
};

/**
 * `metrics` are read *after* the resize; `previousClientHeight` is what the pane
 * measured before it. Resizing does not reflow the content, because only the height
 * changes, so scrollHeight is the same on both sides of the change.
 */
export const shouldPinToBottom = (
  metrics: ScrollMetrics,
  previousClientHeight: number,
  threshold: number = NEAR_BOTTOM_PX
): boolean => {
  const lost = previousClientHeight - metrics.clientHeight;
  if (lost <= 0) return false;

  const distanceFromBottomAfter = metrics.scrollHeight - metrics.scrollTop - metrics.clientHeight;

  return distanceFromBottomAfter - lost < threshold;
};

export const isAtBottom = (metrics: ScrollMetrics, threshold: number = NEAR_BOTTOM_PX): boolean =>
  metrics.scrollHeight - metrics.scrollTop - metrics.clientHeight < threshold;

/**
 * How long after a wheel, touch, key or pointer press a scroll still counts as the reader's own.
 * Scrolls outside it are the code's - the load's own scroll to the bottom, or the browser clamping
 * when the content is replaced - and never decide whether the reader wants the bottom.
 */
export const USER_SCROLL_WINDOW_MS = 1000;

/**
 * Whether a scroll was the reader's: one inside the window after they last touched the pane.
 * Only those may turn following the bottom off - or back on, by reaching the bottom again.
 */
export const isUserScroll = (now: number, lastIntentAt: number, windowMs: number = USER_SCROLL_WINDOW_MS): boolean =>
  now - lastIntentAt <= windowMs;

/** Breathing room under the last comment once the pane stops at the composer. */
export const OVERLAY_GAP_PX = 8;

/**
 * Inline bottom margin and padding for the pane. Where the composer overlays it, the
 * margin moves the pane's bottom edge up to the composer's top, and the padding drops
 * from the stylesheet's 80px (`pb-20`, which existed only to clear the composer) to a
 * small gap. Empty strings hand both back to the stylesheet, as on desktop.
 */
export const overlayLayout = (
  composerHeight: number,
  overlays: boolean
): { marginBottom: string; paddingBottom: string } =>
  overlays
    ? { marginBottom: `${Math.round(composerHeight)}px`, paddingBottom: `${OVERLAY_GAP_PX}px` }
    : { marginBottom: '', paddingBottom: '' };

/**
 * Observes both elements, because only one of them changes size on any given
 * breakpoint: the pane on desktop, the composer on mobile.
 *
 * Writing the margin below does resize the pane, which fires the observer once more;
 * the second pass computes the same values and writes nothing, so it settles.
 *
 * Returns the teardown, so an effect can hand it straight back to React.
 */
export type ConversationScrollPin = {
  stop: () => void;
  /** Follow the bottom again and go there now - a ticket's activity was just (re)opened. */
  stick: () => void;
  /** Whether it is following the bottom, so a pin re-attached to a replaced pane can carry it on. */
  isFollowing: () => boolean;
};

/**
 * Keeps the reader at the bottom of the activity until they scroll up themselves.
 *
 * Opening a ticket loads its activity in several passes - the ticket, the Only-comments setting,
 * the comment permissions - each replacing the content, some via loading placeholders, and picture
 * previews take their real size later still. Deciding "was the reader at the bottom" from the scroll
 * position across that went wrong both ways: a pass measured against placeholders left the pane
 * short, and the corrections showed as the pane jumping. So following the bottom is a state: on
 * when the activity opens, off only when the reader scrolls up, on again when they scroll back down.
 * While it is on, every resize - of the pane, the composer or the content - puts the pane at the
 * bottom inside the ResizeObserver callback, which runs before the browser paints.
 */
export const observeConversationComposer = (
  scrollEl: HTMLElement | null,
  composerEl: HTMLElement | null,
  startFollowing: boolean = true
): ConversationScrollPin => {
  if (!scrollEl || typeof ResizeObserver === 'undefined') {
    return { stop: () => undefined, stick: () => undefined, isFollowing: () => startFollowing };
  }

  let previousClientHeight = scrollEl.clientHeight;
  let following = startFollowing;
  let lastIntentAt = -Infinity;
  const toBottom = () => { scrollEl.scrollTop = scrollEl.scrollHeight; };

  const onIntent = () => { lastIntentAt = Date.now(); };
  const onScroll = () => {
    if (!isUserScroll(Date.now(), lastIntentAt)) return;
    following = isAtBottom({
      scrollHeight: scrollEl.scrollHeight,
      scrollTop: scrollEl.scrollTop,
      clientHeight: scrollEl.clientHeight,
    });
  };

  const sync = () => {
    const metrics: ScrollMetrics = {
      scrollHeight: scrollEl.scrollHeight,
      scrollTop: scrollEl.scrollTop,
      clientHeight: scrollEl.clientHeight,
    };

    const followTheBottom = following || isAtBottom(metrics) || shouldPinToBottom(metrics, previousClientHeight);

    if (composerEl) {
      const next = overlayLayout(
        composerEl.getBoundingClientRect().height,
        getComputedStyle(composerEl).position === 'fixed'
      );

      if (scrollEl.style.marginBottom !== next.marginBottom) scrollEl.style.marginBottom = next.marginBottom;
      if (scrollEl.style.paddingBottom !== next.paddingBottom) scrollEl.style.paddingBottom = next.paddingBottom;
    }

    if (followTheBottom) toBottom();

    previousClientHeight = scrollEl.clientHeight;
  };

  // The pane's content - the comments - changing size. Its children are watched directly, and
  // re-watched when React swaps them out, since the pane itself keeps its size.
  const contentObserver = new ResizeObserver(() => { if (following) toBottom(); });
  const watchContent = () => {
    contentObserver.disconnect();
    Array.from(scrollEl.children).forEach((child) => contentObserver.observe(child));
  };
  const childrenObserver = typeof MutationObserver === 'undefined' ? null : new MutationObserver(watchContent);
  childrenObserver?.observe(scrollEl, { childList: true });
  watchContent();

  const intents = ['wheel', 'touchmove', 'keydown', 'pointerdown'] as const;
  intents.forEach((type) => scrollEl.addEventListener(type, onIntent, { passive: true }));
  scrollEl.addEventListener('scroll', onScroll, { passive: true });

  const observer = new ResizeObserver(sync);
  observer.observe(scrollEl, { box: 'border-box' });
  if (composerEl) observer.observe(composerEl, { box: 'border-box' });

  return {
    stop: () => {
      observer.disconnect();
      contentObserver.disconnect();
      childrenObserver?.disconnect();
      intents.forEach((type) => scrollEl.removeEventListener(type, onIntent));
      scrollEl.removeEventListener('scroll', onScroll);
    },
    stick: () => {
      following = true;
      toBottom();
    },
    isFollowing: () => following,
  };
};
